import React from 'react';
import { motion } from 'framer-motion';
import { Users, Award, Shield, Heart, Target, Globe } from 'lucide-react';
import GlassCard from '../components/GlassCard';

const AboutPage = ({ lang = 'en' }) => {
  const isAr = lang === 'ar';

  const content = {
    title: isAr ? 'عن سوبر كلاسيكي' : 'About Super Classy',
    subtitle: isAr
      ? 'تأسست سوبر كلاسيكي في عام ٢٠١٨، وأعادت تعريف خدمات غسيل السيارات المتنقلة في الكويت. نحن نجمع بين الخدمة الفاخرة والراحة، لنقدم عناية فائقة بالسيارات مباشرة عند باب بيتك.'
      : 'Founded in 2018, Super Classy has redefined mobile car wash services in Kuwait. We combine luxury service with convenience, bringing premium automotive care directly to your doorstep.',
    stats: [
      { label: isAr ? 'سيارة مخدومة' : 'Cars Serviced', value: '5,000+' },
      { label: isAr ? 'فني مختص' : 'Expert Staff', value: '15+' },
      { label: isAr ? 'نسبة الرضا' : 'Satisfaction', value: '100%' },
    ],
    missionTitle: isAr ? 'رسالتنا' : 'Our Mission',
    missionDesc: isAr
      ? 'تقديم خدمات العناية بالسيارات المتنقلة الأكثر راحة وجودة في الكويت، وتجاوز توقعات العملاء مع تعزيز الممارسات المستدامة في صيانة السيارات.'
      : 'To provide Kuwait with the most convenient, premium-quality mobile car care services that exceed customer expectations while promoting sustainable practices in automotive maintenance.',
    visionTitle: isAr ? 'رؤيتنا' : 'Our Vision',
    visionDesc: isAr
      ? 'أن نصبح الاسم الأكثر ثقة في الكويت في مجال العناية بالسيارات، ووضع معايير جديدة للخدمات المتنقلة وتوسيع نطاق خدماتنا المتميزة في جميع دول مجلس التعاون الخليجي.'
      : "To become Kuwait's most trusted name in automotive care, setting new standards for mobile services and expanding our premium offerings across the GCC region.",
    valuesTitle: isAr ? 'قيمنا' : 'Our Values',
    valuesSubtitle: isAr
      ? 'المبادئ التي توجه كل ما نقوم به'
      : 'The principles that guide everything we do',
    teamTitle: isAr ? 'فريق العمل' : 'Meet Our Team',
    teamSubtitle: isAr
      ? 'محترفون ذوو خبرة مكرسون للتميز'
      : 'Experienced professionals dedicated to excellence',
    journeyTitle: isAr ? 'رحلتنا' : 'Our Journey',
  };

  const team = [
    {
      name: isAr ? 'أحمد د. ح. ف. العجمي' : 'Ahmad DH F Al-Ajmi',
      role: isAr ? 'المؤسس والرئيس التنفيذي' : 'Founder & CEO',
      experience: isAr ? '١٥+ عاماً من الخبرة' : '15+ years in automotive care',
      image: '/assets/images/team/ahmed.jpg',
    },
    {
      name: isAr ? 'محمد نفري' : 'Mohamed Nafree',
      role: isAr ? 'رئيس العمليات' : 'Head of Operations',
      experience: isAr
        ? '١٠+ سنوات من الخبرة'
        : '10+ years in service management',
      image: '/assets/images/team/mohammed.jpg',
    },
    {
      name: isAr ? 'فرجانة ج. م.' : 'farjana JM',
      role: isAr ? 'علاقات العملاء' : 'Customer Relations',
      experience: isAr ? '٨+ سنوات من الخبرة' : '8+ years in customer service',
      image: '/assets/images/team/sarah.jpg',
    },
  ];

  const values = [
    {
      icon: <Shield className='h-8 w-8' />,
      title: isAr ? 'ضمان الجودة' : 'Quality Assurance',
      description: isAr
        ? 'نستخدم المنتجات الفاخرة فقط ونتبع معايير جودة صارمة'
        : 'We use only premium products and follow strict quality standards',
    },
    {
      icon: <Heart className='h-8 w-8' />,
      title: isAr ? 'العميل أولاً' : 'Customer First',
      description: isAr
        ? 'رضاكم هو أولويتنا القصوى دائماً'
        : 'Your satisfaction is our top priority',
    },
    {
      icon: <Target className='h-8 w-8' />,
      title: isAr ? 'الدقة' : 'Precision',
      description: isAr
        ? 'الاهتمام بأدق التفاصيل في كل خدمة نقدمها'
        : 'Attention to detail in every service we provide',
    },
    {
      icon: <Globe className='h-8 w-8' />,
      title: isAr ? 'صديق للبيئة' : 'Eco-Friendly',
      description: isAr
        ? 'ممارسات مستدامة وتقنية توفير المياه'
        : 'Sustainable practices and water-saving technology',
    },
  ];

  const milestones = [
    {
      year: '2018',
      event: isAr
        ? 'تأسيس سوبر كلاسيكي لغسيل السيارات'
        : 'Founded Super Classy Car Wash',
    },
    {
      year: '2019',
      event: isAr
        ? 'التوسع لتغطية كافة مناطق الكويت'
        : 'Expanded to full Kuwait coverage',
    },
    {
      year: '2020',
      event: isAr
        ? 'إدخال خدمات النانو سيراميك'
        : 'Introduced ceramic coating services',
    },
    {
      year: '2021',
      event: isAr ? 'خدمة أكثر من ١٠,٠٠٠ عميل' : 'Served 10,000+ customers',
    },
    {
      year: '2022',
      event: isAr
        ? 'إطلاق برنامج العضوية VIP'
        : 'Launched VIP membership program',
    },
    {
      year: '2023',
      event: isAr
        ? 'تحقيق تقييم عملاء ٤.٩/٥'
        : 'Achieved 4.9/5 customer rating',
    },
  ];

  return (
    <div className='pt-32' dir={isAr ? 'rtl' : 'ltr'}>
      {/* Hero Section */}
      <section className='section-padding py-20'>
        <div className='max-w-7xl mx-auto'>
          <div className='grid lg:grid-cols-2 gap-12 items-center'>
            <motion.div
              initial={{ opacity: 0, x: isAr ? 50 : -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className={isAr ? 'text-right' : 'text-left'}
            >
              <h1 className='text-5xl md:text-7xl font-bold mb-6'>
                <span className='text-gradient'>{isAr ? 'حول' : 'About'}</span>
                <br />
                <span className='dark:text-white'>Super Classy</span>
              </h1>
              <p className='text-xl text-gray-600 dark:text-gray-400 mb-8 leading-relaxed'>
                {content.subtitle}
              </p>
              <div
                className={`flex items-center gap-8 ${isAr ? 'justify-start' : ''}`}
              >
                {content.stats.map((stat, i) => (
                  <React.Fragment key={i}>
                    <div className='text-center'>
                      <div className='text-3xl font-bold text-gradient'>
                        {stat.value}
                      </div>
                      <div className='text-sm font-medium text-gray-500'>
                        {stat.label}
                      </div>
                    </div>
                    {i < content.stats.length - 1 && (
                      <div className='h-12 w-px bg-gray-300 dark:bg-gray-700' />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: isAr ? -50 : 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className='relative'
            >
              <GlassCard className='p-2 rounded-3xl'>
                <img
                  src='/assets/images/about/team.jpg'
                  alt='Super Classy Team superclassykw'
                  className='w-full h-96 object-cover rounded-2xl'
                />
              </GlassCard>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className='section-padding py-20 from-gray-50 to-white dark:from-gray-900 dark:to-black'>
        <div className='max-w-7xl mx-auto'>
          <div className='grid md:grid-cols-2 gap-8'>
            <GlassCard className='p-8' lang={lang}>
              <div className='inline-flex p-3 premium-gradient rounded-xl mb-6'>
                <Target className='h-8 w-8 text-white' />
              </div>
              <h3 className='text-2xl font-bold mb-4'>
                {content.missionTitle}
              </h3>
              <p className='text-gray-600 dark:text-gray-400 leading-relaxed'>
                {content.missionDesc}
              </p>
            </GlassCard>

            <GlassCard className='p-8' lang={lang}>
              <div className='inline-flex p-3 premium-gradient rounded-xl mb-6'>
                <Globe className='h-8 w-8 text-white' />
              </div>
              <h3 className='text-2xl font-bold mb-4'>{content.visionTitle}</h3>
              <p className='text-gray-600 dark:text-gray-400 leading-relaxed'>
                {content.visionDesc}
              </p>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className='section-padding py-20'>
        <div className='max-w-7xl mx-auto'>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className='text-center mb-12'
          >
            <h2 className='text-4xl md:text-5xl font-bold mb-4 text-gradient'>
              {content.teamTitle}
            </h2>
            <p className='text-gray-600 dark:text-gray-400 max-w-2xl mx-auto'>
              {content.teamSubtitle}
            </p>
          </motion.div>

          <div className='grid md:grid-cols-3 gap-8'>
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <GlassCard className='p-6 text-center' lang={lang}>
                  <div className='w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden border-2 border-royal-purple-500/20'>
                    <img
                      src={member.image}
                      alt={member.name}
                      className='w-full h-full object-cover'
                    />
                  </div>
                  <h3 className='text-xl font-bold mb-1'>{member.name}</h3>
                  <div className='text-royal-purple-600 dark:text-royal-purple-400 font-bold mb-2'>
                    {member.role}
                  </div>
                  <p className='text-gray-600 dark:text-gray-400 text-sm'>
                    {member.experience}
                  </p>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Journey */}
      <section className='section-padding py-20 from-gray-50 to-white dark:from-gray-900 dark:to-black'>
        <div className='max-w-7xl mx-auto'>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className='text-center mb-16'
          >
            <h2 className='text-4xl md:text-5xl font-bold mb-4 text-gradient'>
              {content.journeyTitle}
            </h2>
          </motion.div>

          <div className='relative'>
            <div className='absolute left-1/2 transform -translate-x-1/2 h-full w-px bg-gradient-to-b from-royal-purple-500 to-transparent' />

            {milestones.map((milestone, index) => {
              const isEven = index % 2 === 0;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: isEven ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex items-center mb-12 ${isEven ? (isAr ? 'flex-row' : 'flex-row-reverse') : isAr ? 'flex-row-reverse' : 'flex-row'}`}
                >
                  <div
                    className={`w-1/2 ${isEven ? (isAr ? 'pl-8 text-left' : 'pr-8 text-right') : isAr ? 'pr-8 text-right' : 'pl-8 text-left'}`}
                  >
                    <GlassCard className='p-6 inline-block' lang={lang}>
                      <div className='text-2xl font-bold text-gradient mb-2'>
                        {milestone.year}
                      </div>
                      <p className='text-gray-700 dark:text-gray-300 font-medium'>
                        {milestone.event}
                      </p>
                    </GlassCard>
                  </div>

                  <div className='w-4 h-4 premium-gradient rounded-full relative z-10 shadow-[0_0_15px_rgba(118,111,210,0.5)]' />

                  <div className='w-1/2' />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
